import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { ToastHost } from '@/design-system'

/**
 * Cached data is deliberately short-lived. Social permissions can change at any
 * moment, and a stale cache would keep showing a collection somebody has just
 * closed. (§117)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        const message = error instanceof Error ? error.message : ''
        // Never retry a refusal — it will keep being refused.
        if (message.includes('permitted') || message.includes('authenticated')) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: true,
    },
  },
})

export function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ToastHost />
    </QueryClientProvider>
  )
}

/** Called after any change to the social graph, so nothing stale survives it. */
export function invalidateSocial() {
  void queryClient.invalidateQueries({ queryKey: ['profile'] })
  void queryClient.invalidateQueries({ queryKey: ['relations'] })
  void queryClient.invalidateQueries({ queryKey: ['follow-requests'] })
  void queryClient.invalidateQueries({ queryKey: ['discover'] })
  void queryClient.invalidateQueries({ queryKey: ['home-summary'] })
  void queryClient.invalidateQueries({ queryKey: ['collection'] })
  void queryClient.invalidateQueries({ queryKey: ['map'] })
}
